import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Upload, FileText, X, AlertCircle } from 'lucide-react';
import { inventoryApi } from '../../api/services/inventory.api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { DialogClose, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const certificateSchema = z.object({
  certificateNumber: z.string().min(1, 'Certificate number is required'),
  type: z.enum([
    'MILL_TEST_CERTIFICATE',
    'CERTIFICATE_OF_CONFORMANCE',
    'API_Q1',
    'API_Q2',
    'NACE_MR0175',
    'ISO_CERTIFICATION',
    'MTR',
    'COC',
    'COA',
    'CALIBRATION',
    'SAFETY',
    'ENVIRONMENTAL',
    'QUALITY',
    'OTHER',
  ] as const),
  subType: z.string().optional(),
  name: z.string().optional(),
  issuedBy: z.string().min(1, 'Issuing authority is required'),
  issuedDate: z.string().min(1, 'Issue date is required'),
  expiryDate: z.string().optional(),
  issuingAuthority: z.string().optional(),
  manufacturerName: z.string().optional(),
  heatNumber: z.string().optional(),
  lotNumber: z.string().optional(),
  complianceStandards: z.array(z.string()).optional(),
  notes: z.string().optional(),
  itemId: z.string().optional(),
});

type CertificateFormData = z.infer<typeof certificateSchema>;

interface CertificateUploadFormProps {
  onSuccess?: () => void;
}

export function CertificateUploadForm({ onSuccess }: CertificateUploadFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<CertificateFormData>({
    resolver: zodResolver(certificateSchema),
    defaultValues: {
      type: 'MILL_TEST_CERTIFICATE',
      complianceStandards: [],
    },
  });

  // Fetch inventory items for selection
  const { data: itemsData } = useQuery({
    queryKey: ['inventory', 'items'],
    queryFn: () => inventoryApi.getAll({ limit: 1000 }),
  });

  const createMutation = useMutation({
    mutationFn: async (data: CertificateFormData) => {
      const formData = new FormData();

      if (file) {
        formData.append('document', file);
      }

      // Append other fields
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value.toString());
          }
        }
      });

      const response = await fetch('http://localhost:3000/api/certificates', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('weatherford_access_token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to upload certificate');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] });
      toast.success('Certificate uploaded successfully');
      form.reset();
      setFile(null);
      onSuccess?.();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upload certificate');
    },
  });

  const validateFile = (selectedFile: File): boolean => {
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    const maxSize = 25 * 1024 * 1024; // 25MB

    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error('Invalid file type. Allowed: PDF, JPG, PNG, XLSX');
      return false;
    }

    if (selectedFile.size > maxSize) {
      toast.error('File size exceeds 25MB limit');
      return false;
    }

    return true;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && validateFile(droppedFile)) {
      setFile(droppedFile);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && validateFile(selectedFile)) {
      setFile(selectedFile);
    }
  };

  const onSubmit = (data: CertificateFormData) => {
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }
    createMutation.mutate(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Dialog Header for Accessibility */}
        <DialogHeader>
          <DialogTitle>Upload Certificate</DialogTitle>
          <DialogDescription>
            Upload a material certificate with supporting documentation and metadata for
            traceability
          </DialogDescription>
        </DialogHeader>

        {/* File Upload Area */}
        <div
          className={cn(
            'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition',
            isDragging
              ? 'border-status-info-border bg-status-info-subtle'
              : 'border-border hover:border-border',
            file && 'border-status-success-border bg-status-success-subtle'
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileText className="h-12 w-12 text-status-success-foreground" />
              <div className="text-left">
                <p className="font-medium">{file.name}</p>
                <p className="text-sm text-muted-foreground">{(file.size / 1024).toFixed(2)} KB</p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  className="mt-1"
                >
                  <X className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-lg font-medium">Drop certificate file here</p>
              <p className="text-sm text-muted-foreground mt-1">
                or click to browse (PDF, JPG, PNG, XLSX - max 25MB)
              </p>
            </div>
          )}
          <input
            id="file-input"
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.xlsx"
            onChange={handleFileChange}
          />
        </div>

        {/* Certificate Metadata Form */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="certificateNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Certificate Number *</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., MTC-2024-001234" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Certificate Type *</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="MILL_TEST_CERTIFICATE">
                      Mill Test Certificate (MTC)
                    </SelectItem>
                    <SelectItem value="CERTIFICATE_OF_CONFORMANCE">
                      Certificate of Conformance (CoC)
                    </SelectItem>
                    <SelectItem value="API_Q1">API Q1 Certification</SelectItem>
                    <SelectItem value="API_Q2">API Q2 Certification</SelectItem>
                    <SelectItem value="NACE_MR0175">NACE MR0175 / ISO 15156</SelectItem>
                    <SelectItem value="ISO_CERTIFICATION">ISO Certification</SelectItem>
                    <SelectItem value="MTR">Material Test Report</SelectItem>
                    <SelectItem value="COC">Certificate of Compliance</SelectItem>
                    <SelectItem value="COA">Certificate of Analysis</SelectItem>
                    <SelectItem value="CALIBRATION">Calibration Certificate</SelectItem>
                    <SelectItem value="SAFETY">Safety Certificate</SelectItem>
                    <SelectItem value="ENVIRONMENTAL">Environmental Certificate</SelectItem>
                    <SelectItem value="QUALITY">Quality Certificate</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="subType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>EN 10204 Type (Optional)</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select EN type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="2.1">2.1 - Supplier Declaration</SelectItem>
                    <SelectItem value="2.2">2.2 - Test Report</SelectItem>
                    <SelectItem value="3.1">3.1 - Manufacturer Certificate</SelectItem>
                    <SelectItem value="3.2">3.2 - Third-Party Certificate</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>For mill test certificates (EN 10204 standard)</FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="itemId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Material / Item (Optional)</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select item" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {itemsData?.data?.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name} ({item.sku})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="issuedDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Issue Date *</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="expiryDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Expiry Date (Optional)</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormDescription>Leave blank if the certificate does not expire</FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="issuedBy"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Issuing Authority *</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., Bureau Veritas, DNV GL" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="manufacturerName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Manufacturer / Mill Name (Optional)</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., Vallourec, Tenaris" {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="heatNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Heat Number (Optional)</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., H123456" {...field} />
                </FormControl>
                <FormDescription>Steel heat number for traceability</FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lotNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Lot Number (Optional)</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., LOT-2024-001" {...field} />
                </FormControl>
                <FormDescription>Batch/lot identifier</FormDescription>
              </FormItem>
            )}
          />
        </div>

        {/* Notes */}
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes (Optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Additional notes or comments about this certificate..."
                  className="resize-none"
                  rows={3}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {/* Warning if no file */}
        {!file && (
          <div className="flex items-start gap-2 p-3 bg-status-warning-subtle border border-status-warning-border rounded-md">
            <AlertCircle className="h-5 w-5 text-status-warning-foreground mt-0.5" />
            <div className="text-sm text-status-warning-foreground">
              <p className="font-medium">No file selected</p>
              <p className="text-xs mt-1">Please upload a certificate document to continue.</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" disabled={!file || createMutation.isPending}>
            {createMutation.isPending ? (
              <>
                <Upload className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload Certificate
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
